import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Alert,
  IconButton,
  Collapse
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SyncIcon from '@mui/icons-material/Sync';
import { useTranslation } from 'react-i18next';

const SyncProgress = ({ syncProgress, onClose }) => {
  const { t } = useTranslation();
  if (!syncProgress) return null;

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'syncing':
        return <SyncIcon color="primary" />;
      default:
        return <SyncIcon color="primary" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'syncing':
        return 'info';
      default:
        return 'info';
    }
  };

  const getProgressValue = (status) => {
    switch (status) {
      case 'starting':
        return 10;
      case 'syncing':
        return 50;
      case 'completed':
        return 100;
      case 'error':
        return 100;
      default:
        return 0;
    }
  };

  // Map status to translation keys
  const statusTitleKey = {
    starting: 'tables.wallets.sync.walletSyncInProgress',
    syncing: 'tables.wallets.sync.walletSyncInProgress',
    completed: 'tables.wallets.sync.walletSyncCompleted',
    error: 'tables.wallets.sync.walletSyncError',
  };

  const statusMessageKey = {
    starting: 'tables.wallets.sync.starting',
    syncing: 'tables.wallets.sync.syncing',
    completed: 'tables.wallets.sync.completedMsg',
    error: 'tables.wallets.sync.errorMsg',
  };

  const title = t(statusTitleKey[syncProgress.status] || 'tables.wallets.sync.walletSyncInProgress');
  const message = syncProgress.message ? t(syncProgress.message, syncProgress.message) : t(statusMessageKey[syncProgress.status] || 'tables.wallets.sync.syncing');

  return (
    <Collapse in={!!syncProgress}>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" flex={1}>
              {getStatusIcon(syncProgress.status)}
              <Box ml={2} flex={1}>
                <Typography variant="h6" component="div">
                  {title}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {message}
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          {syncProgress.status !== 'completed' && syncProgress.status !== 'error' && (
            <Box mt={2}>
              <LinearProgress 
                variant="determinate" 
                value={getProgressValue(syncProgress.status)}
                color={getStatusColor(syncProgress.status)}
              />
            </Box>
          )}
          
          {syncProgress.status === 'error' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
          
          {syncProgress.status === 'completed' && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {message}
            </Alert>
          )}
        </CardContent>
      </Card>
    </Collapse>
  );
};

export default SyncProgress; 